import { Action, actionCreators } from "../actions";
import { Data } from "../service/Data";
import { debugChaletLoginUrl, discoverUrl } from "../utils/config";

export type CustomEnv = "prod" | "testing" | "staging" | "default";
export type ServerEnv = "prod" | "testing" | "staging" | "dev";

export type FailedMessage = {
    roomId: string;
    id: string;
    text: string;
    author_user: Data.AuthorUser;
};

const debugFlags = {
    hot_reload: process.env.NODE_ENV !== "production",
    redux_debug: process.env.NODE_ENV !== "production",
    perf_clock: false,
    show_design: false,
    scroll_logs: false,
    badge_logs: false,
    show_seen_eye: false,
    manual_seen: false,
    show_app_dev_tools: false,
    system_notification_sound: false,
    server_mentions_add_local: false,
    process_disabled: false,
    short_mute: false,
    redux_logger: false,
    async_redux: false,
    async_redux_idle: false,
    async_redux_sparse: false,
    map_to_props_log: false,

    // profile
    disable_notifications: false,
    mute_notification_sound: false,
};

export const initialStore = {
    failedMessages: [] as FailedMessage[],
    customEnvironment: "default" as CustomEnv,

    token: undefined as string | undefined,

    debug_mode: false,
    debug_flags: debugFlags,
    debug_flags_at_start: debugFlags,
};

export type Store = typeof initialStore;

export type Flags = Store["debug_flags"];
export type Flag = keyof Flags;

export function restore(ls?: typeof localStorage): OuterStore {
    let init = { ...initialStore };
    try {
        if (ls) {
            const keys = Object.keys(init).filter(isValidKey);
            keys.forEach(key => {
                const value = ls.getItem(key);
                if (key === "token") {
                    init[key] = value || undefined;
                } else if (value) {
                    try {
                        const parsed = JSON.parse(value);
                        if (isValueValid(key, parsed)) {
                            init = { ...init, [key]: parsed };
                        }
                    } catch (e) {
                        console.error("Failed to load failed messages from LS", e);
                    }
                }
            });
        }
        init.debug_flags = Object.assign(debugFlags, init.debug_flags);
        init.debug_flags_at_start = init.debug_flags;
    } catch (e) {
        console.error("failed to restore localStorage", e);
    }
    return { persist: init };
}

export function update(store = initialStore, action: Action): Store {
    switch (action.type) {
        case actionCreators.onLogout.type: {
            return initialStore;
        }
        case actionCreators.onWrongToken.type: {
            return { ...store, token: undefined };
        }
        case actionCreators.toggleFlag.type: {
            const key = action.payload;
            const value = !store.debug_flags[key];
            const flags = { ...store.debug_flags };
            flags[key] = value;
            if (key === "async_redux" && value === false) {
                flags["async_redux_idle"] = false;
                flags["async_redux_sparse"] = false;
            }
            if (key === "async_redux_idle" && value === true) {
                flags["async_redux"] = true;
                flags["async_redux_sparse"] = false;
            }
            if (key === "async_redux_sparse" && value === true) {
                flags["async_redux"] = true;
                flags["async_redux_idle"] = false;
            }
            return {
                ...store,
                debug_flags: flags,
            };
        }
        case actionCreators.storeObject.type: {
            const { payload } = action;

            return {
                ...store,
                ...payload,
            };
        }
        default: {
            return store;
        }
    }
}

function isValueValid(key: keyof Store, value: unknown) {
    if (key === "failedMessages") {
        return Array.isArray(value);
    } else if (key === "customEnvironment") {
        const v = value as CustomEnv;
        return v === "prod" || v === "testing" || v === "staging";
    } else if (key === "debug_mode") {
        return typeof value === "boolean";
    } else if (key === "debug_flags") {
        if (typeof value === "object" && value) {
            const key: Flag = "disable_notifications";
            return key in value;
        }
    }
    return false;
}

export function isValidKey(k: string): k is keyof Store {
    return k in initialStore;
}

export type OuterStore = { persist: Store };

export function getDebugMode(store: OuterStore) {
    return store.persist.debug_mode;
}

export function getShowDeveloperOptions(store: OuterStore) {
    const env = getCustomEnv(store);
    return env === "prod" ? store.persist.debug_mode : true;
}

export function getFlags(store: OuterStore) {
    return store.persist.debug_flags;
}

export function getFlag(store: OuterStore, key: Flag) {
    return store.persist.debug_flags[key];
}

export function getRequiresAppReload(store: OuterStore) {
    const { debug_flags: flags, debug_flags_at_start: old } = store.persist;
    return (
        flags.redux_debug !== old.redux_debug || // redux debuger plugin
        (flags.async_redux || flags.redux_logger) !== (old.async_redux || old.redux_logger) || // redux re-render notification wrapper
        flags.map_to_props_log !== old.map_to_props_log || // mapStoreToProps wrapper
        false
    );
}

export function getToken(store: OuterStore) {
    return store.persist.token;
}

export function getPeristent(store: OuterStore) {
    return store.persist;
}

export function getCustomEnv({ persist: { customEnvironment: env } }: OuterStore) {
    return env;
}

export function getDiscoverUrl(store: OuterStore) {
    const env = getCustomEnv(store);
    return env === "prod"
        ? "https://api.global.chalet.8x8.com/discover/v2"
        : env === "testing"
        ? "https://api.global-test.chalet.8x8.com/discover/v2"
        : env === "staging"
        ? "https://api.global-staging.chalet.8x8.com/discover/v2"
        : discoverUrl;
}

export function getGlobalUrlBase(store: OuterStore) {
    return getDiscoverUrl(store)
        .split("/")
        .splice(0, 3)
        .join("/");
}

export function getDebugChaletLoginUrl(store: OuterStore) {
    const env = getCustomEnv(store);
    return env === "prod"
        ? "https://client.global.chalet.8x8.com"
        : env === "testing"
        ? "https://client.global-test.chalet.8x8.com"
        : env === "staging"
        ? "https://client.global-staging.chalet.8x8.com"
        : debugChaletLoginUrl;
}

export function getAccessTokenUrl(store: OuterStore) {
    const env = getServerEnv(store);
    return env === "prod"
        ? "https://sso.8x8.com/login/vom"
        : env === "staging"
        ? "https://sso-staging.8x8pilot.com/login/vom-pilot"
        : "https://sso.8x8pilot.com/login/vom-pilot";
}

export function getServerEnv(store: OuterStore): ServerEnv {
    // describes what server environment is used. It will have proper value when "default" custom env is used
    const url = getDiscoverUrl(store);
    return url === "https://api.global.chalet.8x8.com/discover/v2"
        ? "prod"
        : url === "https://api.global-staging.chalet.8x8.com/discover/v2"
        ? "staging"
        : url === "https://api.global-test.chalet.8x8.com/discover/v2"
        ? "testing"
        : "dev";
}
